import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'
import cn from 'classnames'
import Typography from '../Typography'
import useReformattedSchedules from './use-reformatted-schedules'
import { dbDateFormat } from '../../utils/functions'
import { isShoobDayKey, stripShoobDaySuffix } from './shoob-day'
import dayOptionLabel from './day-option-label'
import classes from './shooting-schedules.module.sass'
import { customStyles } from '../_pages/schedule-appointment';

const APPT_LENGTH = 300_000

const ShootingSchedules = ({
  schedules, setAppt, selectedOption, initialSelectedDay, initialSelectedTime, school,
  setIsApptAtShoob, setHasSelectedDay
}) => {
  const [selectedDay, setSelectedDay] = useState(initialSelectedDay)
  const [selectedTime, setSelectedTime] = useState(initialSelectedTime)
  // Server already span-checked the bookable start-times for this option, so the
  // hook just groups them by day — no interval / sitting-length math on the client.
  const reformattedSchedules = useReformattedSchedules(schedules, school && school.id)

  // Which location a day is at is known as soon as the day is picked (independent
  // of time), and can change BOTH ways as the customer changes their mind -- this
  // used to only ever latch to `true`, so picking a Shoob day and then switching
  // back to a school day left `isApptAtShoob` stuck true (wrong schoolId at booking).
  useEffect(() => {
    setIsApptAtShoob(isShoobDayKey(selectedDay && selectedDay.value))
    setHasSelectedDay(!!selectedDay)
  }, [selectedDay])

  useEffect(() => {
    if (selectedDay && selectedTime) {
      const [timeValue, suffix] = selectedTime.value.split(' ')
      let [hours, minutes] = timeValue.split(':')

      if (suffix === 'PM' && hours !== '12') {
        hours = (parseInt(hours, 10) + 12).toString()
      }

      if (hours.length === 1) hours = `0${hours}`

      const formattedTime = `${hours}:${minutes}:00`
      const dayValue = stripShoobDaySuffix(selectedDay.value)

      setAppt(dbDateFormat(`${dayValue}T${formattedTime}+00:00`))
    }
    else setAppt(null)
  }, [selectedDay, selectedTime])

  if (Object.keys(reformattedSchedules).length === 0) return null

  const isMobile = navigator.maxTouchPoints > 1

  return (
    <div>
      <label>
        <Typography>Select Date</Typography>
        {
          <Select
            className={cn(classes.apptSelect, classes.mb2)}
            styles={customStyles}
            placeholder="Choose one"
            value={selectedDay}
            onChange={(opt) => {
              setSelectedDay(opt)
              setSelectedTime(null)
            }}
            options={Object.keys(reformattedSchedules).map((day) => (
              { value: day, label: dayOptionLabel(day, school) }
            ))}
          />
        }
      </label>
      {selectedDay && (() => {
        const timeslots = reformattedSchedules[selectedDay.value] || []

        if (timeslots.length === 0) {
          return (
            <>
              <Typography>Select Time</Typography>
              <p className={classes.noTimesMessage}>
                Sorry, this day is fully booked! Please choose another date to see available times.
              </p>
            </>
          )
        }

        return (
          <>
            <Typography>Select Time</Typography>
            {isMobile ? (
              <select
                className={classes.nativeSelect}
                value={selectedTime && selectedTime.value}
                onChange={(evt) => {
                  if (evt.target.value === '') return

                  setSelectedTime({ value: evt.target.value, label: '' })
                }}
              >
                <option value="" disabled selected>Choose one</option>
                {timeslots.map((timeslot) => {
                  const fiveMinutesFromStart = new Date(Date.parse(`August 19, 1975 ${timeslot.time}`) + (selectedOption.appointmentValue * 60 * 1000))

                  const time = fiveMinutesFromStart.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })

                  return <option value={timeslot.time}>{`${timeslot.time} - ${time}`}</option>
                })}
              </select>
            ) : (
              <Select
                styles={customStyles}
                placeholder="Choose one"
                value={selectedTime}
                onChange={(opt) => setSelectedTime(opt)}
                options={timeslots.map((timeslot) => {
                  const fiveMinutesFromStart = new Date(Date.parse(`August 19, 1975 ${timeslot.time}`) + (selectedOption.appointmentValue * 60 * 1000))

                  const time = fiveMinutesFromStart.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })

                  return { value: timeslot.time, label: `${timeslot.time} - ${time}` }
                })}
              />
            )}
          </>
        )
      })()}
    </div>
  )
}

ShootingSchedules.propTypes = {
  schedules: PropTypes.array.isRequired,
  setAppt: PropTypes.func.isRequired,
  setIsApptAtShoob: PropTypes.func,
  setHasSelectedDay: PropTypes.func,
}

ShootingSchedules.defaultProps = {
  setIsApptAtShoob: () => {},
  setHasSelectedDay: () => {},
}

export default ShootingSchedules
